import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import { useAdmin } from '@/contexts/AdminContext';
import axios from 'axios';
import { ArrowLeft, Upload, Trash2, Copy, Check, Image as ImageIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminMedia = () => {
  const { isAdmin, loading: authLoading } = useAdmin();
  const navigate = useNavigate();
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [copiedId, setCopiedId] = useState(null);

  const adminPassword = localStorage.getItem('adminPassword') || '';
  const authHeader = { 'X-Admin-Password': adminPassword };

  const fetchUploads = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/uploads`, { headers: authHeader });
      setUploads(resp.data);
    } catch (err) {
      console.error('Error loading uploads', err);
      setError('Impossibile caricare la galleria.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (isAdmin) {
      fetchUploads();
    }
  }, [isAdmin, authLoading, fetchUploads]);

  // Wait for auth context to restore session before redirecting
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/admin/login" replace />;
  }

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setError('');
    setUploading(true);

    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    const maxSize = 15 * 1024 * 1024;

    try {
      for (const file of files) {
        if (!allowed.includes(file.type)) {
          setError(`"${file.name}": formato non supportato.`);
          continue;
        }
        if (file.size > maxSize) {
          setError(`"${file.name}": troppo grande (max 15MB).`);
          continue;
        }
        const fd = new FormData();
        fd.append('file', file);
        await axios.post(`${API}/uploads/image`, fd, {
          headers: { ...authHeader, 'Content-Type': 'multipart/form-data' },
        });
      }
      await fetchUploads();
    } catch (err) {
      console.error('Upload error', err);
      setError(err.response?.data?.detail || 'Errore durante il caricamento.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Eliminare "${item.original_filename}"? L'immagine non sarà più disponibile.`)) return;
    try {
      await axios.delete(`${API}/uploads/${item.id}`, { headers: authHeader });
      setUploads(prev => prev.filter(u => u.id !== item.id));
    } catch (err) {
      console.error('Delete error', err);
      alert('Errore durante l\'eliminazione.');
    }
  };

  const handleCopy = async (item) => {
    try {
      await navigator.clipboard.writeText(item.image_url);
      setCopiedId(item.id);
      setTimeout(() => setCopiedId(null), 1800);
    } catch {
      // Fallback
      const input = document.createElement('input');
      input.value = item.image_url;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopiedId(item.id);
      setTimeout(() => setCopiedId(null), 1800);
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return '';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="admin-media-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/admin/dashboard"
            className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-4"
          >
            <ArrowLeft className="mr-2" size={20} />
            Torna alla Dashboard
          </Link>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Galleria Foto</h1>
              <p className="text-gray-600 mt-1">Gestisci tutte le foto caricate per gli eventi</p>
            </div>

            <label
              htmlFor="media-upload-input"
              className={`inline-flex items-center justify-center px-6 py-3 rounded-lg font-semibold cursor-pointer transition-colors ${uploading ? 'bg-gray-300 text-gray-600' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
              data-testid="media-upload-btn"
            >
              <Upload className="mr-2" size={20} />
              {uploading ? 'Caricamento...' : 'Carica foto'}
            </label>
            <input
              id="media-upload-input"
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              multiple
              onChange={handleUpload}
              disabled={uploading}
              className="hidden"
              data-testid="media-upload-input"
            />
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg" data-testid="media-error">
            {error}
          </div>
        )}

        {/* Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : uploads.length === 0 ? (
          <div className="bg-white rounded-xl shadow p-12 text-center" data-testid="media-empty">
            <ImageIcon size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600 text-lg mb-2">Nessuna foto caricata</p>
            <p className="text-gray-500 text-sm">Clicca "Carica foto" per iniziare. Puoi selezionare più file insieme.</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-600 mb-4" data-testid="media-count">
              {uploads.length} {uploads.length === 1 ? 'foto' : 'foto'}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-testid="media-grid">
              {uploads.map((item) => (
                <div
                  key={item.id}
                  className="bg-white rounded-xl shadow hover:shadow-lg transition-shadow overflow-hidden flex flex-col"
                  data-testid={`media-item-${item.id}`}
                >
                  <div className="aspect-square bg-gray-100 overflow-hidden">
                    <img
                      src={item.image_url}
                      alt={item.original_filename}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-3 flex flex-col flex-1">
                    <p className="text-xs font-medium text-gray-900 truncate" title={item.original_filename}>
                      {item.original_filename}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {formatDate(item.created_at)} · {formatBytes(item.size)}
                    </p>
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => handleCopy(item)}
                        className={`flex-1 inline-flex items-center justify-center text-xs px-2 py-2 rounded-lg font-medium transition-colors ${copiedId === item.id ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                        data-testid={`copy-url-btn-${item.id}`}
                        title="Copia URL"
                      >
                        {copiedId === item.id ? <Check size={14} className="mr-1" /> : <Copy size={14} className="mr-1" />}
                        {copiedId === item.id ? 'Copiato' : 'URL'}
                      </button>
                      <button
                        onClick={() => handleDelete(item)}
                        className="inline-flex items-center justify-center text-xs px-2 py-2 rounded-lg font-medium bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                        data-testid={`delete-media-btn-${item.id}`}
                        title="Elimina"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminMedia;
